import type { GuideEvent } from "@shared/epg-guide";
import type { TvLineup, TvLineupChannel } from "../../../hooks/useTvLineup";
import { demoChannelLogo, demoStill } from "../tv/demoArt";

/**
 * A line-up for the design preview and for the tests (`TV-4`).
 *
 * Built around a `now` the caller passes rather than around `Date.now()`, for
 * the same reason the model functions take one: this screen's whole behaviour
 * turns on where the clock sits relative to the schedule, and a fixture that
 * reads the wall clock can only be tested by luck.
 *
 * It deliberately carries the four cases a real hotel has and a happy-path
 * fixture does not:
 *
 *  1. a channel with a full schedule (the case everybody builds for),
 *  2. a channel whose schedule has a hole over right now,
 *  3. an **unmapped** channel — events `[]`, one window-wide gap, which is
 *     exactly what upstream answers for a channel nobody mapped (`BE-22`),
 *  4. a channel with no media, which the screen must refuse out loud rather
 *     than draw as a black rectangle.
 */

const HOUR = 3_600_000;

function event(
  channelAssetId: string,
  id: number,
  title: string,
  synopsis: string,
  from: number,
  to: number
): GuideEvent {
  return {
    epgEventId: id,
    externalId: `epg_${channelAssetId}_${Math.floor(from / 1000)}`,
    channelAssetId,
    programAssetId: `prog-${id}`,
    startScheduled: new Date(from).toISOString(),
    endScheduled: new Date(to).toISOString(),
    durationSeconds: Math.round((to - from) / 1000),
    title,
    titleLanguage: "en",
    synopsis,
    image: { url: demoStill(title), type: "thumbnail", language: "en" },
    ingestedAt: new Date(from - 24 * HOUR).toISOString(),
  };
}

export function demoLineup(now: Date = new Date()): TvLineup {
  const t = now.getTime();
  // Anchor the schedule to the top of the current hour so the fixture always
  // has a programme that started in the past and ends in the future.
  const top = t - (t % HOUR);

  const svt1: TvLineupChannel = {
    id: "ch-svt1",
    number: 1,
    title: "SVT1",
    logoUrl: demoChannelLogo("SVT1"),
    channelAssetId: "asset-svt1",
    contentAssetId: "asset-svt1",
    events: [
      event(
        "asset-svt1",
        101,
        "Morgonstudion",
        "News and conversation to start the day.",
        top - HOUR,
        top
      ),
      event(
        "asset-svt1",
        102,
        "Rapport",
        "The day in fifteen minutes.",
        top,
        top + HOUR / 2
      ),
      event(
        "asset-svt1",
        103,
        "Antikrundan",
        "Valuers tour the country's attics.",
        top + HOUR / 2,
        top + 2 * HOUR
      ),
    ],
    gaps: [],
  };

  const tv4: TvLineupChannel = {
    id: "ch-tv4",
    number: 4,
    title: "TV4",
    logoUrl: demoChannelLogo("TV4"),
    channelAssetId: "asset-tv4",
    contentAssetId: "asset-tv4",
    // A hole over right now: the schedule exists, and stops covering the
    // present hour. The screen must say the guide is missing, not go blank.
    events: [
      event(
        "asset-tv4",
        201,
        "Nyhetsmorgon",
        "Sofa, coffee, and the morning's headlines.",
        top - 3 * HOUR,
        top - HOUR
      ),
      event(
        "asset-tv4",
        202,
        "Efter fem",
        "The afternoon show.",
        top + 2 * HOUR,
        top + 3 * HOUR
      ),
    ],
    gaps: [
      {
        from: new Date(top - HOUR).toISOString(),
        to: new Date(top + 2 * HOUR).toISOString(),
        seconds: 3 * 3600,
      },
    ],
  };

  const local: TvLineupChannel = {
    id: "ch-local",
    number: 12,
    title: "Åland 24",
    logoUrl: demoChannelLogo("Åland 24"),
    channelAssetId: "asset-local",
    contentAssetId: "asset-local",
    // Unmapped: no events at all, one window-wide gap. This is the shape a
    // channel nobody mapped comes back as.
    events: [],
    gaps: [
      {
        from: new Date(top - 12 * HOUR).toISOString(),
        to: new Date(top + 12 * HOUR).toISOString(),
        seconds: 24 * 3600,
      },
    ],
  };

  const dark: TvLineupChannel = {
    id: "ch-dark",
    number: 31,
    title: "Sport Extra",
    logoUrl: demoChannelLogo("Sport Extra"),
    channelAssetId: "asset-dark",
    // No media of any kind - in the line-up, not playable here.
    events: [
      event(
        "asset-dark",
        401,
        "Ishockey: HIFK – Sport",
        "Live from Vasa.",
        top,
        top + 2 * HOUR
      ),
    ],
    gaps: [],
  };

  return {
    window: {
      from: new Date(top - 12 * HOUR).toISOString(),
      to: new Date(top + 12 * HOUR).toISOString(),
    },
    channels: [svt1, tv4, local, dark],
  };
}
